/**
 * PostHog — embudo regalo cumpleaños (/r/cumple → /thank-you-a-lot).
 * Evento canónico: `birthday_gift_funnel_step` (un funnel en PostHog).
 * Espeja también `donation_*` con contexto de mercado (ARS/MP vs USD/Stripe).
 */
import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { getCumpleAttributionPostHogProps } from "@/lib/birthdayCtaAttribution";
import type { CumpleMarket } from "@/lib/cumplePaymentConfig";
import { getCumpleSessionId } from "@/lib/cumpleFunnel";
import {
  extractEmailDomainPostHog,
  getCheckoutAttributionMetadataPostHog,
  getPostHogSlackSessionPropertiesPostHog,
  identifyDonorByEmailPostHog,
  setUserPropertiesPostHog,
  type DonationTrackContext,
  trackDonationAmountSelectedPostHog,
  trackDonationCheckoutStartedPostHog,
  trackDonationFormSubmittedPostHog,
  trackDonationPageViewPostHog,
  trackDonationPaymentErrorPostHog,
  trackDonationPaymentSuccessPostHog,
} from "@/lib/posthogAnalytics";
import { getWorkerGeoAnalyticsProps } from "@/lib/serverTemplateDetection";
import posthog from "posthog-js";

const CUMPLE_PAGE = "cumple" as const;

export type CumpleRecoverySource = "recovery_email" | "email_query" | "stripe_checkout";

export type BirthdayGiftFunnelStep =
  | "page_enter"
  | "amount_selected"
  | "details_view"
  | "email_captured"
  | "recovery_restored"
  | "form_submitted"
  | "checkout_started"
  | "checkout_opened"
  | "pay_blocked"
  | "stripe_checkout_view"
  | "mp_checkout_view"
  | "mp_redirect"
  | "payment_return_error"
  | "payment_return_pending"
  | "payment_success"
  | "thank_you_view"
  | "thank_you_another_gift"
  | "abandoned";

/** PostHog linkage for payment metadata (webhook → person/session join). */
export function getCumplePaymentLinkageMetadataPostHog(): Record<string, string> {
  const ph = getPostHogSlackSessionPropertiesPostHog();
  // Atribución de origen (landing/entry/referrer/UTM) — el Slack de venta la
  // renderiza para saber desde qué página llegó el regalo.
  const out: Record<string, string> = getCheckoutAttributionMetadataPostHog();
  if (ph.ph_session_id) out.ph_session_id = ph.ph_session_id;
  if (ph.ph_distinct_id) out.ph_distinct_id = ph.ph_distinct_id;
  return out;
}

function cumpleLinkageProps(): { cumple_session_id: string } {
  return { cumple_session_id: getCumpleSessionId() };
}

function isPostHogReady(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.posthog &&
    typeof window.posthog.capture === "function"
  );
}

export function cumpleMarketToTrackContext(market: CumpleMarket): DonationTrackContext {
  return {
    currency: market.currency,
    country: market.country,
    checkout_gateway: market.gateway,
    language: market.language,
    is_argentina: market.isArgentina,
  };
}

function baseFunnelProps(market: CumpleMarket) {
  return {
    site: BRAND_SOURCE_KEY,
    funnel: "cumple",
    product_type: "birthday_gift" as const,
    page: CUMPLE_PAGE,
    page_path: typeof window !== "undefined" ? window.location.pathname : "/r/cumple",
    currency: market.currency,
    country: market.country,
    payment_country: market.country,
    checkout_gateway: market.gateway,
    market_language: market.language,
    is_argentina: market.isArgentina,
    timestamp: new Date().toISOString(),
    ...getCumpleAttributionPostHogProps(),
    ...getWorkerGeoAnalyticsProps(),
    ...getPostHogSlackSessionPropertiesPostHog(),
  };
}

export function trackBirthdayGiftFunnelStepPostHog(
  step: BirthdayGiftFunnelStep,
  market: CumpleMarket,
  extra?: Record<string, string | number | boolean | null | undefined>,
  replayNearNow = false,
) {
  if (!isPostHogReady()) return;

  const replayProps = replayNearNow
    ? getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true })
    : {};

  posthog.capture("birthday_gift_funnel_step", {
    ...baseFunnelProps(market),
    ...replayProps,
    funnel_step: step,
    ...extra,
  });
}

export function trackCumplePageEnterPostHog(
  market: CumpleMarket,
  extra?: { session_id?: string; time_on_page_seconds?: number },
) {
  const ctx = cumpleMarketToTrackContext(market);
  trackDonationPageViewPostHog(CUMPLE_PAGE, ctx);
  trackBirthdayGiftFunnelStepPostHog("page_enter", market, {
    cumple_session_id: extra?.session_id,
    time_on_page_seconds: extra?.time_on_page_seconds ?? 0,
  });
}

export function trackCumpleAmountSelectedPostHog(
  market: CumpleMarket,
  amount: number,
  isCustom: boolean,
  giftTierLabel?: string,
) {
  const ctx: DonationTrackContext = {
    ...cumpleMarketToTrackContext(market),
    is_custom_amount: isCustom,
    ...(giftTierLabel ? { gift_tier: giftTierLabel } : {}),
  };
  trackDonationAmountSelectedPostHog(amount, isCustom, CUMPLE_PAGE, ctx);
  trackBirthdayGiftFunnelStepPostHog("amount_selected", market, {
    amount,
    is_custom_amount: isCustom,
    ...(giftTierLabel ? { gift_tier: giftTierLabel } : {}),
  });
}

export function trackCumpleDetailsViewPostHog(market: CumpleMarket, amount: number) {
  trackBirthdayGiftFunnelStepPostHog("details_view", market, {
    amount,
    ...cumpleLinkageProps(),
  });
}

export function trackCumpleEmailCapturedPostHog(
  market: CumpleMarket,
  email: string,
  amount?: number,
  extra?: {
    capture_source?:
      | "details_form"
      | "amount_optional"
      | "stripe_checkout"
      | "mercadopago_checkout"
      | "recovery";
  },
) {
  const domain = extractEmailDomainPostHog(email);
  identifyDonorByEmailPostHog(email, {
    funnel: "cumple",
    currency: market.currency,
    country: market.country,
    checkout_gateway: market.gateway,
  });
  setUserPropertiesPostHog({
    cumple_email_captured_at: new Date().toISOString(),
    ...(domain ? { email_domain: domain } : {}),
  });
  trackBirthdayGiftFunnelStepPostHog(
    "email_captured",
    market,
    {
      amount,
      has_email: true,
      donor_email_domain: domain ?? undefined,
      capture_source: extra?.capture_source ?? "details_form",
      recovery_eligible: true,
      ...cumpleLinkageProps(),
    },
    true,
  );

  if (isPostHogReady()) {
    posthog.capture("donation_email_captured", {
      ...baseFunnelProps(market),
      ...getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true }),
      funnel_step: "email_captured",
      amount,
      has_email: true,
      donor_email_domain: domain ?? undefined,
      capture_source: extra?.capture_source ?? "details_form",
      recovery_eligible: true,
      page: CUMPLE_PAGE,
    });
  }
}

/** Fan volvió por link de recovery (?recover= o ?email=). */
export function trackCumpleRecoveryRestoredPostHog(
  market: CumpleMarket,
  email: string,
  source: CumpleRecoverySource,
  extra?: { amount?: number; donation_id?: string | null },
) {
  const domain = extractEmailDomainPostHog(email);
  identifyDonorByEmailPostHog(email, {
    funnel: "cumple",
    recovery_source: source,
    currency: market.currency,
    country: market.country,
  });
  setUserPropertiesPostHog({
    cumple_recovery_restored_at: new Date().toISOString(),
    cumple_recovery_source: source,
    ...(domain ? { email_domain: domain } : {}),
  });
  trackBirthdayGiftFunnelStepPostHog(
    "recovery_restored",
    market,
    {
      recovery_source: source,
      has_email: true,
      donor_email_domain: domain ?? undefined,
      amount: extra?.amount,
      donation_id: extra?.donation_id ?? undefined,
      ...cumpleLinkageProps(),
    },
    true,
  );
}

export function trackCumpleFormSubmittedPostHog(
  market: CumpleMarket,
  amount: number,
  hasName: boolean,
  messageLength: number,
  hasEmail: boolean,
  donorEmail?: string,
) {
  const emailDomain = donorEmail ? extractEmailDomainPostHog(donorEmail) : undefined;
  if (donorEmail?.trim()) {
    identifyDonorByEmailPostHog(donorEmail, {
      funnel: "cumple",
      currency: market.currency,
      country: market.country,
      checkout_gateway: market.gateway,
    });
  }
  const ctx: DonationTrackContext = {
    ...cumpleMarketToTrackContext(market),
    has_name: hasName,
    has_message: messageLength > 0,
    has_email: hasEmail,
    ...(emailDomain ? { donor_email_domain: emailDomain } : {}),
    ...cumpleLinkageProps(),
  };
  trackDonationFormSubmittedPostHog(amount, hasName, messageLength, CUMPLE_PAGE, ctx);
  trackBirthdayGiftFunnelStepPostHog(
    "form_submitted",
    market,
    {
      amount,
      has_name: hasName,
      has_message: messageLength > 0,
      has_email: hasEmail,
      message_length: messageLength,
    },
    true,
  );
}

export function trackCumpleCheckoutStartedPostHog(
  market: CumpleMarket,
  amount: number,
  paymentReference: string,
  extra?: {
    donation_id?: string | null;
    has_name?: boolean;
    has_message?: boolean;
    donor_email?: string;
  },
) {
  const emailDomain = extra?.donor_email
    ? extractEmailDomainPostHog(extra.donor_email)
    : undefined;
  if (extra?.donor_email?.trim()) {
    identifyDonorByEmailPostHog(extra.donor_email, {
      funnel: "cumple",
      currency: market.currency,
      country: market.country,
      checkout_gateway: market.gateway,
      checkout_started_at: new Date().toISOString(),
    });
  }
  const ctx: DonationTrackContext = {
    ...cumpleMarketToTrackContext(market),
    payment_reference: paymentReference,
    donation_id: extra?.donation_id,
    has_name: extra?.has_name,
    has_message: extra?.has_message,
    has_email: !!extra?.donor_email?.trim(),
    ...(emailDomain ? { donor_email_domain: emailDomain } : {}),
    ...cumpleLinkageProps(),
  };
  trackDonationCheckoutStartedPostHog(amount, paymentReference, CUMPLE_PAGE, ctx);
  trackBirthdayGiftFunnelStepPostHog(
    "checkout_started",
    market,
    {
      amount,
      payment_reference: paymentReference,
      donation_id: extra?.donation_id ?? undefined,
      has_name: extra?.has_name,
      has_message: extra?.has_message,
    },
    true,
  );
}

export function trackCumpleStripeCheckoutViewPostHog(
  market: CumpleMarket,
  amount: number,
  donationId: string | null,
) {
  trackBirthdayGiftFunnelStepPostHog(
    "stripe_checkout_view",
    market,
    { amount, donation_id: donationId ?? undefined },
    true,
  );
}

/** Payment Brick embebido (sin redirect a MP Checkout Pro). */
export function trackCumpleMpCheckoutViewPostHog(
  market: CumpleMarket,
  amount: number,
  donationId: string | null,
  preferenceId: string,
) {
  trackBirthdayGiftFunnelStepPostHog(
    "mp_checkout_view",
    market,
    { amount, donation_id: donationId ?? undefined, preference_id: preferenceId },
    true,
  );
  trackBirthdayGiftFunnelStepPostHog(
    "checkout_opened",
    market,
    {
      amount,
      donation_id: donationId ?? undefined,
      preference_id: preferenceId,
      checkout_mode: "embedded_brick",
    },
    true,
  );
}

/** Usuario intentó pagar pero falta un requisito (ej. email en AR). */
export function trackCumplePayBlockedPostHog(
  market: CumpleMarket,
  reason: "email_required" | "invalid_email",
  amount?: number,
) {
  trackBirthdayGiftFunnelStepPostHog("pay_blocked", market, {
    block_reason: reason,
    amount,
  });
  posthog.capture("cumple_pay_blocked", {
    ...baseFunnelProps(market),
    block_reason: reason,
    amount,
    wizard_step: "details",
  });
}

export function trackCumpleMpRedirectPostHog(
  market: CumpleMarket,
  amount: number,
  donationId: string | null,
) {
  trackBirthdayGiftFunnelStepPostHog(
    "mp_redirect",
    market,
    { amount, donation_id: donationId ?? undefined },
    true,
  );
}

export function trackCumplePaymentErrorPostHog(
  market: CumpleMarket,
  amount: number,
  errorMessage: string,
  extra?: DonationTrackContext,
) {
  const ctx: DonationTrackContext = {
    ...cumpleMarketToTrackContext(market),
    ...extra,
  };
  trackDonationPaymentErrorPostHog(amount, errorMessage, CUMPLE_PAGE, ctx);
  trackBirthdayGiftFunnelStepPostHog(
    "payment_return_error",
    market,
    {
      amount,
      error_message: errorMessage,
      error_stage: extra?.error_stage,
      wizard_step: extra?.wizard_step,
      time_on_page_seconds: extra?.time_on_page_seconds,
    },
    true,
  );
}

export function trackCumplePaymentPendingPostHog(market: CumpleMarket, amount: number) {
  trackBirthdayGiftFunnelStepPostHog("payment_return_pending", market, { amount });
}

export function trackCumplePaymentSuccessPostHog(
  market: CumpleMarket,
  amount: number,
  donationId: string | null,
  extra?: {
    payment_provider?: "mercadopago" | "stripe";
    page_path?: string;
    has_email?: boolean;
    has_name?: boolean;
    has_message?: boolean;
    time_on_page_seconds?: number;
    donor_email?: string;
    payment_id?: string;
  },
) {
  const emailDomain = extra?.donor_email
    ? extractEmailDomainPostHog(extra.donor_email)
    : undefined;
  const ctx: DonationTrackContext = {
    ...cumpleMarketToTrackContext(market),
    donation_id: donationId,
    payment_provider: extra?.payment_provider,
    has_email: extra?.has_email ?? !!extra?.donor_email?.trim(),
    has_name: extra?.has_name,
    has_message: extra?.has_message,
    time_on_page_seconds: extra?.time_on_page_seconds,
    payment_id: extra?.payment_id,
    ...(emailDomain ? { donor_email_domain: emailDomain } : {}),
    ...cumpleLinkageProps(),
  };
  trackDonationPaymentSuccessPostHog(
    amount,
    donationId,
    CUMPLE_PAGE,
    ctx,
    extra?.donor_email,
  );
  trackBirthdayGiftFunnelStepPostHog(
    "payment_success",
    market,
    {
      amount,
      donation_id: donationId ?? undefined,
      payment_provider: extra?.payment_provider,
      page_path: extra?.page_path ?? window.location.pathname,
      has_email: extra?.has_email,
      has_name: extra?.has_name,
      has_message: extra?.has_message,
      time_on_page_seconds: extra?.time_on_page_seconds,
    },
    true,
  );
}

export function trackCumpleThankYouViewPostHog(
  market: CumpleMarket,
  extra?: {
    from_payment_success?: boolean;
    payment_provider?: "mercadopago" | "stripe";
  },
) {
  trackBirthdayGiftFunnelStepPostHog("thank_you_view", market, {
    page_path: "/thank-you-a-lot",
    from_payment_success: extra?.from_payment_success ?? false,
    payment_provider: extra?.payment_provider,
  });
}

export function trackCumpleThankYouAnotherGiftPostHog(market: CumpleMarket) {
  trackBirthdayGiftFunnelStepPostHog("thank_you_another_gift", market, {
    page_path: "/thank-you-a-lot",
  });
}

export function trackCumpleAbandonedPostHog(
  market: CumpleMarket,
  extra: {
    last_wizard_step: string;
    time_on_page_seconds: number;
    checkout_started: boolean;
    session_id?: string;
    amount?: number;
    has_email?: boolean;
  },
) {
  trackBirthdayGiftFunnelStepPostHog(
    "abandoned",
    market,
    {
      wizard_step: extra.last_wizard_step,
      time_on_page_seconds: extra.time_on_page_seconds,
      checkout_started: extra.checkout_started,
      cumple_session_id: extra.session_id,
      ...(extra.amount != null && extra.amount > 0 ? { amount: extra.amount } : {}),
      ...(extra.has_email != null ? { has_email: extra.has_email } : {}),
    },
    true,
  );
}